/**
 * NDJSON streaming client for AgentCore Gateway. Returns an async iterator
 * of typed StreamEvents. Supports client-side cancellation via AbortController.
 */
import { getRuntimeConfig } from "../runtime-config";

export type StreamEvent =
  | { type: "token"; text: string }
  | { type: "round"; round: 1 | 2 | 3; status: "starting" | "complete" }
  | { type: "agent"; agent: "claude" | "gemini" | "grok"; status: "thinking" | "streaming" | "done" | "error"; error?: string }
  | { type: "reasoning"; chosen: { agent: string; subquestion: string; why: string }[] }
  | { type: "transcript"; round: 1 | 2 | 3; agent: "claude" | "gemini" | "grok"; text: string }
  | { type: "final"; text: string }
  | { type: "error"; message: string; retryable: boolean }
  | { type: "rate-limit-warning"; remaining: number; limit: number };

/** Events emitted by the async Deep debate transcript stream. */
export type DebateStreamEvent =
  | { type: "debate-meta"; debateId: string; question: string; status: string; createdAt: string }
  | { type: "turn"; seq: string; round: 1 | 2 | 3 | "synth"; agent: "claude" | "gemini" | "grok" | "collaborator"; text: string; createdAt: string }
  | { type: "status"; status: "complete" | "failed" | "timeout"; errorMessage?: string };

export interface StreamOptions {
  idToken: string;
  /** Optional invite code — sent as `X-Demo-Code`. Non-admin users need
   *  to set this to the value the admin configured before they can chat. */
  demoCode?: string | null;
  signal?: AbortSignal;
}

export async function* streamAgent(
  path: string,
  body: unknown,
  opts: StreamOptions,
): AsyncIterable<StreamEvent> {
  const cfg = getRuntimeConfig();
  // Chat routes go through the Lambda Function URL (RESPONSE_STREAM).
  // Trim the trailing slash CDK emits on Function URLs so path composition
  // yields a single `/`.
  const base = (cfg.streamUrl ?? cfg.gatewayUrl).replace(/\/$/, "");
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${opts.idToken}`,
  };
  if (opts.demoCode) headers["X-Demo-Code"] = opts.demoCode;
  const res = await fetch(`${base}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal: opts.signal,
  });

  if (res.status === 429) {
    // Per-user daily rate limit — headers carry the shape
    const limit = Number(res.headers.get("x-ratelimit-limit") ?? 0);
    const resetAt = res.headers.get("x-ratelimit-reset") ?? "";
    throw Object.assign(new Error("rate-limited"), { kind: "rate-limit", limit, resetAt });
  }
  if (res.status === 401) {
    // Two paths land here: missing/bad invite code, or expired/missing JWT.
    // The body's `error` text distinguishes them for the UI.
    const text = await res.text().catch(() => "");
    throw Object.assign(new Error("unauthorized"), { kind: "unauthorized", body: text });
  }
  if (res.status === 503) {
    // Kill switch off, or global daily cap hit. Same surface to the user:
    // "demo paused, try again later." Body carries the specific reason.
    const text = await res.text().catch(() => "");
    throw Object.assign(new Error("demo-unavailable"), { kind: "demo-unavailable", body: text });
  }
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => "");
    throw new Error(`Request failed: ${res.status} ${text.slice(0, 200)}`);
  }
  const warnHeader = res.headers.get("x-ratelimit-warn");
  if (warnHeader) {
    const [remaining, limit] = warnHeader.split("/").map(Number);
    yield { type: "rate-limit-warning", remaining, limit };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      try {
        yield JSON.parse(line) as StreamEvent;
      } catch {
        /* skip malformed */
      }
    }
  }
}

/**
 * POST /debate — submit an async Deep debate. Returns the debate id; the
 * frontend then opens streamDebateTranscript() to receive turns as they
 * land in DynamoDB.
 */
export async function submitDebate(
  question: string,
  opts: { idToken: string; demoCode?: string | null },
): Promise<{ debateId: string }> {
  const cfg = getRuntimeConfig();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${opts.idToken}`,
  };
  if (opts.demoCode) headers["X-Demo-Code"] = opts.demoCode;
  const r = await fetch(`${cfg.profileApiUrl}/debate`, {
    method: "POST",
    headers,
    body: JSON.stringify({ question }),
  });
  if (r.status === 401) {
    const text = await r.text().catch(() => "");
    throw Object.assign(new Error("unauthorized"), { kind: "unauthorized", body: text });
  }
  if (r.status === 429) {
    const limit = Number(r.headers.get("x-ratelimit-limit") ?? 0);
    const resetAt = r.headers.get("x-ratelimit-reset") ?? "";
    throw Object.assign(new Error("rate-limited"), { kind: "rate-limit", limit, resetAt });
  }
  if (r.status === 503) {
    const text = await r.text().catch(() => "");
    throw Object.assign(new Error("demo-unavailable"), { kind: "demo-unavailable", body: text });
  }
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(`POST /debate failed: ${r.status} ${text.slice(0, 200)}`);
  }
  return await r.json() as { debateId: string };
}

/**
 * Tail a debate's transcript stream via the Function URL. Yields turn events
 * as they're written to DDB by the worker; closes when the worker marks the
 * debate complete/failed/timeout.
 */
export async function* streamDebateTranscript(
  debateId: string,
  opts: { idToken: string; signal?: AbortSignal },
): AsyncIterable<DebateStreamEvent> {
  const cfg = getRuntimeConfig();
  const base = (cfg.streamUrl ?? cfg.gatewayUrl).replace(/\/$/, "");
  const r = await fetch(`${base}/debate/${encodeURIComponent(debateId)}/stream`, {
    method: "GET",
    headers: { Authorization: `Bearer ${opts.idToken}` },
    signal: opts.signal,
  });
  if (r.status === 401) throw Object.assign(new Error("unauthorized"), { kind: "unauthorized" });
  if (r.status === 403) throw Object.assign(new Error("forbidden"), { kind: "forbidden" });
  if (r.status === 404) throw Object.assign(new Error("not-found"), { kind: "not-found" });
  if (!r.ok || !r.body) {
    const text = await r.text().catch(() => "");
    throw new Error(`Stream request failed: ${r.status} ${text.slice(0, 200)}`);
  }
  const reader = r.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).trim();
      buf = buf.slice(nl + 1);
      if (!line) continue;
      try {
        yield JSON.parse(line) as DebateStreamEvent;
      } catch { /* skip malformed */ }
    }
  }
}

/**
 * User profile management (Settings panel).
 * These hit the Gateway's /profile routes which are backed by a thin Lambda.
 */
export interface AdminConfigPayload {
  turnLimit: number;
  demoModeEnabled: boolean;
  inviteCode: string | null;
  dailyDebateQuotaUser: number | null;
  dailyDebateQuotaGlobal: number | null;
  /** Live: how many requests have hit the global counter today. */
  globalUsageToday: number;
  resetAt: string;
  isAdmin: boolean;
  defaults: { turnLimit: number; maxTurnLimit: number };
}

/** Partial-update body for PUT /admin/config — only fields present are changed. */
export interface AdminConfigPatch {
  turnLimit?: number;
  demoModeEnabled?: boolean;
  inviteCode?: string | null;
  dailyDebateQuotaUser?: number | null;
  dailyDebateQuotaGlobal?: number | null;
}

export interface ProfileApi {
  get(): Promise<unknown>;
  setExplicitPreferences(prefs: string[]): Promise<void>;
  delete(): Promise<void>;
  export(): Promise<Blob>;
  resetAgentHistory(agent: string): Promise<void>;
  resetAllHistory(): Promise<void>;
  usage(): Promise<{ singleAgent: number; quick: number; deep: number }>;
  endSession(agent: string, sessionId: string): Promise<void>;
  getAdminConfig(): Promise<AdminConfigPayload>;
  setAdminConfig(patch: AdminConfigPatch): Promise<{ ok: boolean } & AdminConfigPayload>;
}

export function profileApi(idToken: string): ProfileApi {
  const cfg = getRuntimeConfig();
  const headers = { Authorization: `Bearer ${idToken}`, "Content-Type": "application/json" };
  const call = async (path: string, init?: RequestInit) => {
    const r = await fetch(`${cfg.profileApiUrl}${path}`, { ...init, headers: { ...headers, ...(init?.headers ?? {}) } });
    if (!r.ok) throw new Error(`${path} failed: ${r.status}`);
    return r;
  };
  return {
    get:           async () => (await call("/profile")).json(),
    setExplicitPreferences: async (prefs) => void (await call("/profile/preferences", { method: "PUT", body: JSON.stringify({ prefs }) })),
    delete:        async () => void (await call("/profile", { method: "DELETE" })),
    export:        async () => (await call("/profile/export")).blob(),
    resetAgentHistory: async (agent) => void (await call(`/profile/history/${agent}`, { method: "DELETE" })),
    resetAllHistory:   async () => void (await call("/profile/history", { method: "DELETE" })),
    usage:         async () => (await call("/profile/usage")).json(),
    endSession:    async (agent, sessionId) =>
      void (await call("/session/end", { method: "POST", body: JSON.stringify({ agent, sessionId }) })),
    getAdminConfig: async () => (await call("/admin/config")).json(),
    setAdminConfig: async (patch) =>
      (await call("/admin/config", { method: "PUT", body: JSON.stringify(patch) })).json(),
  };
}
